<template>
  <div class="course-card tech-course-card" @click="handleDetail">

    <!-- 封面区 -->
    <div class="cover-img">
      <img
        v-if="course.imgUrl && !imgError"
        :src="baseUrl + course.imgUrl"
        :alt="course.title || '课程封面'"
        @error="imgError = true"
        class="cover-photo"
      />
      <!-- 高级占位封面 -->
      <div v-else class="cover-fallback">
        <div class="fallback-icon">
          <i class="el-icon-film"></i>
        </div>
        <span class="fallback-text">情景课程</span>
        <div class="fallback-grid-bg"></div>
      </div>

      <!-- 渐变遮罩 -->
      <div class="cover-gradient"></div>

      <!-- 左下角角色标签 -->
      <span class="course-badge">
        {{ roles.includes('teacher') ? '课程维护' : '情景学习' }}
      </span>

      <!-- hover 发光条 -->
      <div class="cover-glow-bar"></div>
    </div>

    <!-- 内容区 -->
    <div class="course-body">
      <div class="course-title">{{ course.title || '未命名课程' }}</div>
      <div class="course-desc">
        <span class="course-content">
          {{ course.summary || '围绕典型情景开展案例学习、任务理解和训练复盘。' }}
        </span>
      </div>
      <div class="course-action">
        <span>{{ roles.includes('teacher') ? '进入维护' : '开始学习' }}</span>
        <i class="el-icon-right"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "CourseCard",
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      imgError: false,
    };
  },
  computed: {
    ...mapState(["baseUrl", "roles"]),
  },
  watch: {
    course() {
      this.imgError = false;
    },
  },
  methods: {
    handleDetail() {
      if (!this.course || !this.course.urlCode) return;
      if (this.roles.includes("teacher")) {
        this.$router.push({
          name: "Study",
          params: { id: this.course.urlCode },
        });
      } else {
        this.$router.push({
          name: "StudentClass",
          params: { id: this.course.urlCode },
        });
      }
    },
  },
};
</script>

<style scoped>
/* ─────────────────────────────────────────
   Card shell
───────────────────────────────────────── */
.tech-course-card {
  position: relative;
  width: 360px;
  min-height: 340px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(168, 199, 245, 0.70);
  border-radius: 18px;
  box-sizing: border-box;
  cursor: pointer;
  transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
  box-shadow: 0 10px 28px rgba(8, 28, 66, 0.07);
}

.tech-course-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 54px rgba(8, 28, 66, 0.12);
  border-color: rgba(30, 155, 255, 0.36);
}

/* ─────────────────────────────────────────
   Cover
───────────────────────────────────────── */
.cover-img {
  position: relative;
  height: 190px;
  overflow: hidden;
  background: linear-gradient(135deg, #e8f0fc, #f4f8ff);
}

.cover-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.34s ease;
}

.tech-course-card:hover .cover-photo {
  transform: scale(1.05);
}

/* Premium fallback */
.cover-fallback {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  overflow: hidden;
}

.fallback-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(21, 87, 216, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21, 87, 216, 0.08) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
}

.fallback-icon {
  position: relative;
  z-index: 1;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21, 87, 216, 0.13), rgba(32, 200, 255, 0.16));
  border: 1px solid rgba(21, 87, 216, 0.16);
  color: #1557d8;
  font-size: 24px;
  box-shadow: 0 8px 22px rgba(21, 87, 216, 0.13);
}

.fallback-text {
  position: relative;
  z-index: 1;
  font-size: 12px;
  font-weight: 800;
  color: #1557d8;
  letter-spacing: 0.5px;
}

/* Gradient mask over image */
.cover-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 44%, rgba(8, 20, 52, 0.38));
  pointer-events: none;
}

/* Badge */
.course-badge {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 2;
  padding: 4px 9px;
  border-radius: 999px;
  color: #fff;
  background: rgba(18, 72, 200, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(8px);
  font-size: 11px;
  font-weight: 800;
}

/* Hover glow bar at top of cover */
.cover-glow-bar {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1248c8, #20c8ff, rgba(32, 200, 255, 0));
  opacity: 0;
  transition: opacity 0.24s ease;
  z-index: 4;
}

.tech-course-card:hover .cover-glow-bar {
  opacity: 1;
}

/* ─────────────────────────────────────────
   Body
───────────────────────────────────────── */
.course-body {
  padding: 15px 16px 14px;
}

.course-title {
  min-height: 44px;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #081c42;
  font-size: 15px;
  line-height: 1.4;
  font-weight: 900;
}

.course-desc {
  text-align: left;
}

.course-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #475569;
  font-size: 13px;
  line-height: 1.68;
  min-height: 44px;
}

.course-action {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #1557d8;
  font-size: 13px;
  font-weight: 900;
  transition: color 0.2s;
}

.tech-course-card:hover .course-action {
  color: #1e9bff;
}

@media (max-width: 420px) {
  .tech-course-card {
    width: 100%;
  }
}
</style>
